import {
  MENU_POPOVER_FRAME_CLASS_NAME,
  renderMenuPopoverContent,
} from "@-/dev.improve.haniverse.components.ui.menu-popover-surface";
import type { StyleProp, ViewStyle } from "@-/com.npmjs.react-native";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { NativeOnlyAnimatedView } from "@-/dev.improve.haniverse.components.ui.native-only-animated-view";
import {
  PortalContextBridgeContent,
  usePortalContextValues,
} from "@-/dev.improve.haniverse.components.ui.portal-context-bridge";
import { TextClassContext, Text } from "@-/dev.improve.haniverse.components.ui.text";
import { cn } from "@-/com.npmjs.cn";
import {
  CheckboxItem,
  Content,
  Item,
  ItemIndicator,
  Label,
  Overlay,
  Portal,
  RadioItem,
  Separator,
  SubContent,
  SubTrigger,
  useSubContext,
  type CheckboxItemProps,
  type CheckboxItemRef,
  type ContentProps,
  type ContentRef,
  type ItemProps,
  type ItemRef,
  type LabelProps,
  type LabelRef,
  type RadioItemProps,
  type RadioItemRef,
  type SeparatorProps,
  type SeparatorRef,
  type SubContentProps,
  type SubContentRef,
  type SubTriggerProps,
  type SubTriggerRef,
} from "@rn-primitives/context-menu";
import { Fragment, type ComponentProps, type ReactNode, type RefAttributes } from "react";
import { Platform, StyleSheet, View } from "@-/com.npmjs.react-native";
import { FadeIn } from "@-/com.npmjs.react-native-reanimated";
import { FullWindowOverlay as RNFullWindowOverlay } from "@-/com.npmjs.react-native-screens";

export function ContextMenuSubTrigger({
  children,
  className,
  iconClassName,
  inset,
  ...props
}: SubTriggerProps &
  RefAttributes<SubTriggerRef> & {
    children?: ReactNode;
    iconClassName?: string;
    inset?: boolean;
  }) {
  const { open } = useSubContext();
  let iconName: "LuChevronDown" | "LuChevronRight" | "LuChevronUp" = "LuChevronDown";
  if (Platform.OS === "web") {
    iconName = "LuChevronRight";
  } else if (open) {
    iconName = "LuChevronUp";
  }
  return (
    <TextClassContext
      value={cn(
        "text-sm select-none group-active:text-accent-foreground",
        open && "text-accent-foreground",
      )}
    >
      <SubTrigger
        className={cn(
          "group flex flex-row items-center rounded-xs p-2 active:bg-accent sm:py-1.5",
          Platform.select({
            web: "focus:bg-accent focus:text-accent-foreground cursor-default outline-none [&_svg]:pointer-events-none",
          }),
          open && cn("bg-accent", Platform.select({ native: "mb-1" })),
          inset && "pl-8",
          className,
        )}
        {...props}
      >
        <>{children}</>
        <Icon className={cn("ml-auto size-4 shrink-0 text-foreground", iconClassName)}
          name={iconName}
          size={16}
        />
      </SubTrigger>
    </TextClassContext>
  );
}

export function ContextMenuSubContent({
  className,
  ...props
}: SubContentProps & RefAttributes<SubContentRef>) {
  return (
    <NativeOnlyAnimatedView entering={FadeIn}>
      <SubContent
        className={cn(
          MENU_POPOVER_FRAME_CLASS_NAME,
          "p-1",
          Platform.select({
            web: "animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 fade-in-0 data-[state=closed]:zoom-out-95 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-(--radix-context-menu-content-transform-origin) z-50 min-w-[8rem]",
          }),
          className,
        )}
        {...props}
      >
        {renderMenuPopoverContent(props.children)}
      </SubContent>
    </NativeOnlyAnimatedView>
  );
}

const PLATFORM_COMPONENTS = {
  ["ios" as string]: RNFullWindowOverlay,
};
const FullWindowOverlay = PLATFORM_COMPONENTS[Platform.OS] ?? Fragment;

export function ContextMenuContent({
  className,
  overlayClassName,
  overlayStyle,
  portalHost,
  ...props
}: ContentProps &
  RefAttributes<ContentRef> & {
    overlayClassName?: string;
    overlayStyle?: StyleProp<ViewStyle>;
    portalHost?: string;
  }) {
  const contextValues = usePortalContextValues();
  let resolvedOverlayStyle = overlayStyle;
  if (Platform.OS !== "web") {
    resolvedOverlayStyle =
      overlayStyle === undefined
        ? StyleSheet.absoluteFill
        : StyleSheet.flatten([StyleSheet.absoluteFill, overlayStyle]);
  }

  return (
    <Portal hostName={portalHost}>
      <PortalContextBridgeContent values={contextValues}>
        <FullWindowOverlay>
          <Overlay className={overlayClassName} style={resolvedOverlayStyle}>
            <NativeOnlyAnimatedView entering={FadeIn}>
              <TextClassContext value="text-popover-foreground">
                <Content
                  className={cn(
                    MENU_POPOVER_FRAME_CLASS_NAME,
                    "min-w-32 p-1",
                    Platform.select({
                      web: cn(
                        "z-50 max-h-(--radix-context-menu-content-available-height) origin-(--radix-context-menu-content-transform-origin) cursor-default animate-in fade-in-0 zoom-in-95",
                        props.side === "bottom" && "slide-in-from-top-2",
                        props.side === "top" && "slide-in-from-bottom-2",
                      ),
                    }),
                    className,
                  )}
                  {...props}
                >
                  {renderMenuPopoverContent(props.children)}
                </Content>
              </TextClassContext>
            </NativeOnlyAnimatedView>
          </Overlay>
        </FullWindowOverlay>
      </PortalContextBridgeContent>
    </Portal>
  );
}

export function ContextMenuItem({
  className,
  inset,
  variant,
  ...props
}: ItemProps &
  RefAttributes<ItemRef> & {
    className?: string;
    inset?: boolean;
    variant?: "default" | "destructive";
  }) {
  return (
    <TextClassContext
      value={cn(
        "text-sm text-popover-foreground select-none",
        variant === "destructive" && "text-destructive",
      )}
    >
      <Item
        className={cn(
          "group relative flex flex-row items-center gap-2 rounded-xs p-2 active:bg-accent sm:py-1.5",
          Platform.select({
            web: cn(
              "cursor-default outline-none focus:bg-accent focus:text-accent-foreground data-disabled:pointer-events-none",
              variant === "destructive" && "focus:bg-destructive/10 dark:focus:bg-destructive/20",
            ),
          }),
          variant === "destructive" && "active:bg-destructive/10 dark:active:bg-destructive/20",
          props.disabled && "opacity-50",
          inset && "pl-8",
          className,
        )}
        {...props}
      />
    </TextClassContext>
  );
}

export function ContextMenuCheckboxItem({
  children,
  className,
  ...props
}: CheckboxItemProps &
  RefAttributes<CheckboxItemRef> & {
    children?: ReactNode;
  }) {
  return (
    <TextClassContext value="text-sm text-popover-foreground select-none group-active:text-accent-foreground">
      <CheckboxItem
        className={cn(
          "group relative flex flex-row items-center gap-2 rounded-xs py-2 pr-2 pl-8 active:bg-accent sm:py-1.5",
          Platform.select({
            web: "focus:bg-accent focus:text-accent-foreground cursor-default outline-none data-[disabled]:pointer-events-none",
          }),
          props.disabled && "opacity-50",
          className,
        )}
        {...props}
      >
        <View className="absolute left-2 flex size-3.5 items-center justify-center">
          <ItemIndicator>
            <Icon className={cn(
                "size-4 text-foreground",
                Platform.select({ web: "pointer-events-none" }),
              )}
              name="LuCheck"
              size={16}
            />
          </ItemIndicator>
        </View>
        <>{children}</>
      </CheckboxItem>
    </TextClassContext>
  );
}

export function ContextMenuRadioItem({
  children,
  className,
  ...props
}: RadioItemProps &
  RefAttributes<RadioItemRef> & {
    children?: ReactNode;
  }) {
  return (
    <TextClassContext value="text-sm text-popover-foreground select-none group-active:text-accent-foreground">
      <RadioItem
        className={cn(
          "group relative flex flex-row items-center gap-2 rounded-xs py-2 pr-2 pl-8 active:bg-accent sm:py-1.5",
          Platform.select({
            web: "focus:bg-accent focus:text-accent-foreground cursor-default outline-none data-[disabled]:pointer-events-none",
          }),
          props.disabled && "opacity-50",
          className,
        )}
        {...props}
      >
        <View className="absolute left-2 flex size-3.5 items-center justify-center">
          <ItemIndicator>
            <View className="size-2 rounded-full bg-foreground" />
          </ItemIndicator>
        </View>
        <>{children}</>
      </RadioItem>
    </TextClassContext>
  );
}

export function ContextMenuLabel({
  className,
  inset,
  ...props
}: LabelProps &
  RefAttributes<LabelRef> & {
    className?: string;
    inset?: boolean;
  }) {
  return (
    <Label
      className={cn(
        "p-2 font-sans text-sm font-medium text-foreground sm:py-1.5",
        inset && "pl-8",
        className,
      )}
      {...props}
    />
  );
}

export function ContextMenuSeparator({
  className,
  ...props
}: SeparatorProps & RefAttributes<SeparatorRef>) {
  return <Separator className={cn("-mx-1 my-1 h-px bg-border", className)} {...props} />;
}

export function ContextMenuShortcut({ className, ...props }: ComponentProps<typeof Text>) {
  return (
    <Text
      className={cn("ml-auto font-sans text-xs tracking-widest text-muted-foreground", className)}
      {...props}
    />
  );
}
